import { addMonths, differenceInDays } from 'date-fns';
import React from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { fetchDateRange } from '../utils/api/reports/analytics-requests';
import { DashboardChartHeader } from './components/chart-header';
import { DashboardDatePicker } from './components/dashboard-date-picker';
import { DashboardLineChartType1 } from './components/dashboard-line-chart-type1';

const allStatuses = ['waiting', 'removed', 'registered', 'attended', 'absent'];

function formatData(data) {
  if (data && Object.keys(data)) {
    const byStatus = {};
    for (const [dateVal, statuses] of Object.entries(data)) {
      for (const [status, count] of Object.entries(statuses)) {
        if (!byStatus[status]) {
          byStatus[status] = [];
        }
        byStatus[status].push({ x: dateVal, y: count });
      }
    }
    // Hydrate missing keys
    let allDates = Object.keys(data);
    allDates = [...new Set(allDates)].sort();
    for (const status of allStatuses) {
      // Fill in any gaps in the data.
      if (!byStatus[status]) {
        byStatus[status] = [];
      }
      const statusData = byStatus[status] ?? [];
      const dataByDate = {};
      statusData.forEach((d) => (dataByDate[d.x] = d.y));
      byStatus[status] = allDates.map((d) => ({
        x: d,
        y: dataByDate[d] || 0,
      }));
    }
    return [
      {
        id: 'Waitlist',
        label: 'Waitlist',
        data: byStatus.waiting ?? [],
        //color: "#eeeeee",
      },
      {
        id: 'Declined',
        label: 'Declined',
        data: byStatus.removed ?? [],
        //color: "rgb(232, 168, 56)",
      },
      {
        id: 'Registered',
        label: 'Registered',
        data: byStatus.registered ?? [],
        //color: "rgb(241, 225, 91)",
      },
      {
        id: 'Attended',
        label: 'Attended',
        data: byStatus.attended ?? [],
        //color: "rgb(244, 117, 96)",
      },
      {
        id: 'Absent',
        label: 'Absent',
        data: byStatus.absent ?? [],
        //color: "#61cdbb",
      },
    ];
  }
  return [];
}

export function WorkshopAttendanceChart(props) {
  const defaultDateRange = [addMonths(new Date(), -12), new Date()];
  const [dateRange, setDateRange] = React.useState(defaultDateRange);
  const [data, setData] = React.useState([]);
  const [dateRangeError, setDateRangeError] = React.useState('');
  const [showDatePicker, setShowDatePicker] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      // We delay showing the datepicker until the rest of the UI is ready in order 
      // to avoid a snapping in the UI as it resizes itself.
      setShowDatePicker(true);

      if (!dateRange) {
        // If the date range is empty, force it to the default date range and do it again.
        setDateRange(defaultDateRange);
        return;
      }
      const daysDiff = differenceInDays(dateRange[1], dateRange[0]);
      if (daysDiff > 732) {
        setDateRangeError('Date range too large.  Please select a range of 2 years or less.');
        return;
      }
      setDateRangeError('');
      const jsonData = await fetchDateRange(
        props.reportClient,
        '/api/v1/analytics/workshop-attendance-counts',
        dateRange
      );
      const formattedData = formatData(jsonData);
      setData(formattedData);
    })();
  }, [dateRange]);

  return (
    <div className="chart-box-flow">
      <div className="chart-box-flow-header">
        <DashboardChartHeader
          title="Workshop Engagement"
          tooltip="A breakdown of Workshop engagement."
        />
        <DashboardDatePicker {...{ showDatePicker, dateRange, setDateRange, dateLocale: props.dateLocale }} />
      </div>
      {dateRangeError && <div className="ui message chart-date-error">{dateRangeError}</div>}
      <div className="chart-box-autosizer-wrapper">
        <AutoSizer>
          {({ width, height }) => (
            <DashboardLineChartType1 data={data} width={width} height={height} />
          )}
        </AutoSizer>
      </div>
    </div>
  );
}
